import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Users, Plus, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fetchCircles, createCircle } from "@/lib/api";
import { toast } from "sonner";

const goldIcon = L.divIcon({
  className: "",
  html: `<span style="display:block;width:16px;height:16px;background:#D4AF37;border:2px solid #0F3821;border-radius:50%;box-shadow:0 0 0 4px rgba(212,175,55,0.25)"></span>`,
  iconSize: [16, 16], iconAnchor: [8, 8],
});

const KINDS = ["Community", "Church", "Online", "School"];

const Circles = () => {
  const [circles, setCircles] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", facilitator: "", city: "", country: "", kind: "Community",
    schedule: "", contact_email: "", description: "", lat: "", lng: "",
  });

  const load = () => fetchCircles().then(setCircles).catch(() => {});
  useEffect(() => { load(); }, []);

  const pins = useMemo(
    () => circles.filter((c) => typeof c.lat === "number" && typeof c.lng === "number"),
    [circles]
  );
  const countries = useMemo(() => new Set(circles.map((c) => c.country)).size, [circles]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      payload.lat = form.lat ? parseFloat(form.lat) : null;
      payload.lng = form.lng ? parseFloat(form.lng) : null;
      if (payload.lat === null) delete payload.lat;
      if (payload.lng === null) delete payload.lng;
      if (!payload.schedule) delete payload.schedule;
      if (!payload.description) delete payload.description;
      await createCircle(payload);
      toast.success("Your circle is on the map! Check your email.");
      setForm({ name: "", facilitator: "", city: "", country: "", kind: "Community", schedule: "", contact_email: "", description: "", lat: "", lng: "" });
      setOpen(false);
      load();
    } catch {
      toast.error("Couldn't list your circle. Please check the fields and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-cream min-h-screen pt-32" data-testid="circles-page">
      <section className="bg-emerald text-cream py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow text-gold mb-6">The Global Movement</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-3xl">
              Find your <span className="italic text-gold">circle.</span>
            </h1>
            <p className="font-editorial italic text-2xl text-cream/80 mt-8 max-w-2xl">
              Reader circles and church study groups are gathering around the world. Join one near you — or start your own.
            </p>
            <div className="flex flex-wrap gap-10 mt-10">
              <div><p className="font-display text-4xl text-gold">{circles.length}</p><p className="eyebrow text-cream/60 mt-1">Circles</p></div>
              <div><p className="font-display text-4xl text-gold">{countries}</p><p className="eyebrow text-cream/60 mt-1">Countries</p></div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <h2 className="font-display text-3xl md:text-5xl text-ink">Reader Map</h2>
            <button className="btn-emerald" onClick={() => setOpen((v) => !v)} data-testid="list-circle-btn">
              {open ? <>Close <X className="w-4 h-4" /></> : <><Plus className="w-4 h-4" /> List your circle</>}
            </button>
          </div>

          {open && (
            <form onSubmit={submit} className="border border-line bg-surface p-8 md:p-10 mb-12 grid md:grid-cols-2 gap-6" data-testid="circle-form">
              <div><label className="eyebrow text-ink/50 block mb-2">Circle name</label>
                <input required value={form.name} onChange={set("name")} data-testid="circle-name" className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body text-sm" /></div>
              <div><label className="eyebrow text-ink/50 block mb-2">Facilitator</label>
                <input required value={form.facilitator} onChange={set("facilitator")} data-testid="circle-facilitator" className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body text-sm" /></div>
              <div><label className="eyebrow text-ink/50 block mb-2">City</label>
                <input required value={form.city} onChange={set("city")} data-testid="circle-city" className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body text-sm" /></div>
              <div><label className="eyebrow text-ink/50 block mb-2">Country</label>
                <input required value={form.country} onChange={set("country")} data-testid="circle-country" className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body text-sm" /></div>
              <div><label className="eyebrow text-ink/50 block mb-2">Type</label>
                <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
                  <SelectTrigger data-testid="circle-kind" className="w-full bg-cream border-line rounded-none p-3 h-auto font-body text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-cream">{KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="eyebrow text-ink/50 block mb-2">Contact email</label>
                <input required type="email" value={form.contact_email} onChange={set("contact_email")} data-testid="circle-email" className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body text-sm" /></div>
              <div><label className="eyebrow text-ink/50 block mb-2">Schedule (optional)</label>
                <input value={form.schedule} onChange={set("schedule")} placeholder="e.g. Sundays 4pm" data-testid="circle-schedule" className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body text-sm placeholder:text-ink/30" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="eyebrow text-ink/50 block mb-2">Lat (optional)</label>
                  <input value={form.lat} onChange={set("lat")} placeholder="6.5244" data-testid="circle-lat" className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body text-sm placeholder:text-ink/30" /></div>
                <div><label className="eyebrow text-ink/50 block mb-2">Lng (optional)</label>
                  <input value={form.lng} onChange={set("lng")} placeholder="3.3792" data-testid="circle-lng" className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body text-sm placeholder:text-ink/30" /></div>
              </div>
              <div className="md:col-span-2"><label className="eyebrow text-ink/50 block mb-2">Description (optional)</label>
                <textarea rows={2} value={form.description} onChange={set("description")} data-testid="circle-description" className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body text-sm resize-none" /></div>
              <div className="md:col-span-2">
                <button type="submit" disabled={loading} className="btn-emerald disabled:opacity-60" data-testid="circle-submit">
                  {loading ? "Listing…" : "Add my circle to the map"}
                </button>
                <p className="font-body text-xs text-ink/45 mt-3">Tip: add latitude & longitude to appear as a pin on the map.</p>
              </div>
            </form>
          )}

          <div className="border border-line overflow-hidden" style={{ height: "460px" }} data-testid="reader-map">
            <MapContainer center={[20, 10]} zoom={2} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {pins.map((c) => (
                <Marker key={c.id} position={[c.lat, c.lng]} icon={goldIcon}>
                  <Popup>
                    <strong>{c.name}</strong><br />
                    {c.city}, {c.country} · {c.kind}<br />
                    {c.schedule ? <>{c.schedule}<br /></> : null}
                    Facilitator: {c.facilitator}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line mt-12" data-testid="circle-directory">
            {circles.length === 0 && (
              <div className="bg-cream p-12 text-center md:col-span-2 lg:col-span-3">
                <Users className="w-10 h-10 text-emerald mx-auto mb-4" strokeWidth={1.2} />
                <p className="font-editorial italic text-2xl text-emerald">Be the first to start a circle.</p>
              </div>
            )}
            {circles.map((c) => (
              <div key={c.id} className="bg-cream p-8" data-testid={`circle-card-${c.id}`}>
                <div className="flex items-center gap-2 text-emerald mb-3">
                  <MapPin className="w-4 h-4" /><span className="eyebrow">{c.kind}</span>
                </div>
                <h3 className="font-display text-2xl text-ink mb-1">{c.name}</h3>
                <p className="font-body text-sm text-ink/60">{c.city}, {c.country}</p>
                {c.schedule && <p className="font-body text-sm text-ink/60 mt-1">{c.schedule}</p>}
                {c.description && <p className="font-body text-sm text-ink/70 mt-4 leading-relaxed">{c.description}</p>}
                <p className="font-body text-xs text-ink/45 mt-5">Facilitated by {c.facilitator}</p>
                <a href={`mailto:${c.contact_email}?subject=Joining ${encodeURIComponent(c.name)}`} className="btn-outline !py-2.5 !px-5 text-sm mt-5" data-testid={`circle-join-${c.id}`}>
                  Ask to join
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Circles;
